import { useState, useEffect } from 'react';
import {
  BarChart,
  Bar,
  XAxis,
  YAxis,
  CartesianGrid,
  Tooltip,
  ResponsiveContainer,
  PieChart,
  Pie,
  Cell,
  Legend,
  LineChart,
  Line,
  Area,
  AreaChart,
} from 'recharts';

interface DashboardChartsProps {
  dailyData: { date: string; hours: number }[];
  subjectData: { name: string; value: number }[];
  weeklyTrend?: { week: string; hours: number; posts: number }[];
  monthlyTrend?: { month: string; hours: number; posts: number }[];
  subjectMonthly?: Record<string, unknown>[];
  subjectKeys?: string[];
  layoutMode?: 'normal' | 'compact';
}

const COLORS = [
  '#0d9488', '#14b8a6', '#2dd4bf', '#5eead4', '#99f6e4',
  '#4ade80', '#86efac', '#a7f3d0', '#6ee7b7',
];

const STACKED_COLORS = [
  '#0d9488', '#f59e0b', '#ef4444', '#8b5cf6', '#3b82f6',
  '#ec4899', '#14b8a6', '#f97316', '#6366f1', '#10b981',
];

function useIsMobile() {
  const [isMobile, setIsMobile] = useState(false);
  useEffect(() => {
    const check = () => setIsMobile(window.innerWidth < 640);
    check();
    window.addEventListener('resize', check);
    return () => window.removeEventListener('resize', check);
  }, []);
  return isMobile;
}

type BarTooltipPayload = { value: number }[];
type PieTooltipPayload = { name: string; value: number }[];
type WeeklyTooltipPayload = { dataKey: string; value: number; color: string }[];
type MonthlyTooltipPayload = { dataKey: string; value: number; color: string }[];

const CustomTooltipBar = ({
  active, payload, label,
}: { active?: boolean; payload?: BarTooltipPayload; label?: string }) => {
  if (active && payload && payload.length) {
    return (
      <div className="bg-white border border-gray-200 rounded-lg px-3 py-2 shadow-md max-w-[140px]">
        <p className="text-xs text-gray-500 mb-1 truncate">{label}</p>
        <p className="text-sm font-semibold text-teal-700">{payload[0].value}時間</p>
      </div>
    );
  }
  return null;
};

const CustomTooltipPie = ({
  active, payload,
}: { active?: boolean; payload?: PieTooltipPayload }) => {
  if (active && payload && payload.length) {
    return (
      <div className="bg-white border border-gray-200 rounded-lg px-3 py-2 shadow-md max-w-[160px]">
        <p className="text-xs text-gray-500 mb-1 truncate">{payload[0].name}</p>
        <p className="text-sm font-semibold text-teal-700">{payload[0].value}時間</p>
      </div>
    );
  }
  return null;
};

const CustomTooltipWeekly = ({
  active, payload, label,
}: { active?: boolean; payload?: WeeklyTooltipPayload; label?: string }) => {
  if (active && payload && payload.length) {
    return (
      <div className="bg-white border border-gray-200 rounded-lg px-3 py-2 shadow-md max-w-[160px]">
        <p className="text-xs text-gray-500 mb-1 truncate">{label}</p>
        {payload.map((p) => (
          <p key={p.dataKey} className="text-xs font-semibold" style={{ color: p.color }}>
            {p.dataKey === 'hours' ? `${p.value}時間` : `${p.value}件`}
          </p>
        ))}
      </div>
    );
  }
  return null;
};

const CustomTooltipMonthly = ({
  active, payload, label,
}: { active?: boolean; payload?: MonthlyTooltipPayload; label?: string }) => {
  if (active && payload && payload.length) {
    return (
      <div className="bg-white border border-gray-200 rounded-lg px-3 py-2 shadow-md max-w-[160px]">
        <p className="text-xs text-gray-500 mb-1 truncate">{label}</p>
        {payload.map((p) => (
          <p key={p.dataKey} className="text-xs font-semibold" style={{ color: p.color }}>
            {p.dataKey === 'hours' ? `${p.value}時間` : `${p.value}件`}
          </p>
        ))}
      </div>
    );
  }
  return null;
};

const CustomTooltipStacked = ({
  active, payload, label,
}: {
  active?: boolean;
  payload?: { dataKey: string; value: number; color: string; name: string }[];
  label?: string;
}) => {
  if (active && payload && payload.length) {
    const total = payload.reduce((sum, p) => sum + (p.value || 0), 0);
    return (
      <div className="bg-white border border-gray-200 rounded-lg px-3 py-2 shadow-md max-w-[180px]">
        <p className="text-xs text-gray-500 mb-1.5 truncate">{label}</p>
        {payload.filter(p => p.value > 0).map((p) => (
          <p key={p.dataKey} className="text-xs truncate" style={{ color: p.color }}>
            {p.dataKey}: {p.value}h
          </p>
        ))}
        <p className="text-xs font-semibold text-gray-700 mt-1 pt-1 border-t border-gray-100">
          合計: {Math.round(total * 10) / 10}時間
        </p>
      </div>
    );
  }
  return null;
};

export default function DashboardCharts({
  dailyData = [],
  subjectData = [],
  weeklyTrend,
  monthlyTrend,
  subjectMonthly,
  subjectKeys,
  layoutMode = 'normal',
}: DashboardChartsProps) {
  const isMobile = useIsMobile();
  const hasData = dailyData.length > 0 || subjectData.length > 0;

  if (!hasData) {
    return (
      <div className="bg-gray-100 rounded-xl border border-gray-300 p-8 sm:p-12 text-center">
        <div className="w-14 h-14 flex items-center justify-center bg-gray-200 rounded-full mx-auto mb-4">
          <i className="ri-bar-chart-2-line text-xl text-gray-400"></i>
        </div>
        <p className="text-sm text-gray-500">学習データが蓄積されるとグラフが表示されます</p>
      </div>
    );
  }

  const sortedSubjectData = [...subjectData].sort((a, b) => b.value - a.value);

  // スマホ用のXAxisインターバル計算
  const dailyInterval = isMobile
    ? Math.floor(dailyData.length / 4)
    : dailyData.length > 14 ? Math.floor(dailyData.length / 7) : 0;

  const weeklyInterval = isMobile ? 2 : 0;
  const monthlyInterval = isMobile ? 2 : 0;

  // スマホ用グラフ高さ
  const chartHeightSm = isMobile ? 200 : 256;
  const chartHeightMd = isMobile ? 220 : 288;
  const chartHeightLg = isMobile ? 240 : 320;

  return (
    <div className={`grid grid-cols-1 lg:grid-cols-2 ${layoutMode === 'compact' ? 'gap-3 mb-4' : 'gap-6 mb-8'}`}>
      {/* Row 1: Daily bar — スマホでは全幅 */}
      <div className="bg-gray-100 dark:bg-gray-800 rounded-xl border border-gray-300 dark:border-gray-700 p-4 sm:p-5">
        <h3 className="text-sm font-semibold text-gray-700 dark:text-gray-200 mb-0.5">
          直近30日の学習時間
        </h3>
        <p className="text-xs text-gray-400 dark:text-gray-500 mb-3">日別の学習時間推移</p>
        <div style={{ height: chartHeightSm }}>
          <ResponsiveContainer width="100%" height="100%">
            <BarChart
              data={dailyData}
              margin={{ top: 5, right: 4, left: isMobile ? -20 : -15, bottom: 5 }}
            >
              <CartesianGrid strokeDasharray="3 3" stroke="#e5e7eb" />
              <XAxis
                dataKey="date"
                tick={{ fontSize: isMobile ? 9 : 10, fill: '#9ca3af' }}
                tickLine={false}
                axisLine={{ stroke: '#d1d5db' }}
                interval={dailyInterval}
              />
              <YAxis
                tick={{ fontSize: isMobile ? 9 : 10, fill: '#9ca3af' }}
                tickLine={false}
                axisLine={{ stroke: '#d1d5db' }}
                unit="h"
                width={isMobile ? 28 : 35}
              />
              <Tooltip content={<CustomTooltipBar />} />
              <Bar dataKey="hours" fill="#0d9488" radius={[3, 3, 0, 0]} maxBarSize={isMobile ? 16 : 28} />
            </BarChart>
          </ResponsiveContainer>
        </div>
      </div>

      {/* Row 2: Subject pie — スマホでは全幅・縦積み */}
      <div className="bg-gray-100 dark:bg-gray-800 rounded-xl border border-gray-300 dark:border-gray-700 p-4 sm:p-5">
        <h3 className="text-sm font-semibold text-gray-700 dark:text-gray-200 mb-0.5">
          科目別学習時間
        </h3>
        <p className="text-xs text-gray-400 dark:text-gray-500 mb-3">直近30日の科目別割合</p>
        {isMobile ? (
          /* スマホ: 横並びリスト表示（円グラフは省略してバー表示） */
          <div className="space-y-2">
            {sortedSubjectData.slice(0, 6).map((item, index) => {
              const total = subjectData.reduce((s, d) => s + d.value, 0);
              const pct = total > 0 ? Math.round((item.value / total) * 100) : 0;
              return (
                <div key={item.name} className="flex items-center gap-2">
                  <div
                    className="w-2.5 h-2.5 rounded-full shrink-0"
                    style={{ backgroundColor: COLORS[index % COLORS.length] }}
                  />
                  <span className="text-xs text-gray-600 dark:text-gray-300 w-20 truncate shrink-0">{item.name}</span>
                  <div className="flex-1 bg-gray-200 dark:bg-gray-700 rounded-full h-2">
                    <div
                      className="h-2 rounded-full"
                      style={{ width: `${pct}%`, backgroundColor: COLORS[index % COLORS.length] }}
                    />
                  </div>
                  <span className="text-xs text-gray-500 dark:text-gray-400 w-12 text-right shrink-0">
                    {item.value}h ({pct}%)
                  </span>
                </div>
              );
            })}
          </div>
        ) : (
          <div style={{ height: chartHeightSm }}>
            <ResponsiveContainer width="100%" height="100%">
              <PieChart>
                <Pie
                  data={subjectData}
                  cx="50%"
                  cy="45%"
                  innerRadius={45}
                  outerRadius={75}
                  paddingAngle={3}
                  dataKey="value"
                  nameKey="name"
                  stroke="none"
                >
                  {subjectData.map((_, index) => (
                    <Cell key={`cell-${index}`} fill={COLORS[index % COLORS.length]} />
                  ))}
                </Pie>
                <Tooltip content={<CustomTooltipPie />} />
                <Legend
                  iconType="circle"
                  iconSize={7}
                  formatter={(value: string) => (
                    <span className="text-xs text-gray-600 dark:text-gray-300">{value}</span>
                  )}
                />
              </PieChart>
            </ResponsiveContainer>
          </div>
        )}
      </div>

      {/* Row 3: Monthly Trend */}
      {monthlyTrend && monthlyTrend.length > 0 && (
        <div className="bg-gray-100 dark:bg-gray-800 rounded-xl border border-gray-300 dark:border-gray-700 p-4 sm:p-5">
          <h3 className="text-sm font-semibold text-gray-700 dark:text-gray-200 mb-0.5">
            月別の学習時間推移
          </h3>
          <p className="text-xs text-gray-400 dark:text-gray-500 mb-3">
            直近12ヶ月の月別学習時間と投稿数
          </p>
          <div style={{ height: chartHeightMd }}>
            <ResponsiveContainer width="100%" height="100%">
              <AreaChart
                data={monthlyTrend}
                margin={{ top: 5, right: 4, left: isMobile ? -20 : -15, bottom: 5 }}
              >
                <defs>
                  <linearGradient id="monthlyHoursGrad" x1="0" y1="0" x2="0" y2="1">
                    <stop offset="5%" stopColor="#0d9488" stopOpacity={0.3} />
                    <stop offset="95%" stopColor="#0d9488" stopOpacity={0.02} />
                  </linearGradient>
                  <linearGradient id="monthlyPostsGrad" x1="0" y1="0" x2="0" y2="1">
                    <stop offset="5%" stopColor="#f59e0b" stopOpacity={0.2} />
                    <stop offset="95%" stopColor="#f59e0b" stopOpacity={0.02} />
                  </linearGradient>
                </defs>
                <CartesianGrid strokeDasharray="3 3" stroke="#e5e7eb" />
                <XAxis
                  dataKey="month"
                  tick={{ fontSize: isMobile ? 9 : 10, fill: '#9ca3af' }}
                  tickLine={false}
                  axisLine={{ stroke: '#d1d5db' }}
                  interval={monthlyInterval}
                />
                <YAxis
                  yAxisId="left"
                  tick={{ fontSize: isMobile ? 9 : 10, fill: '#9ca3af' }}
                  tickLine={false}
                  axisLine={{ stroke: '#d1d5db' }}
                  unit="h"
                  width={isMobile ? 28 : 35}
                />
                {!isMobile && (
                  <YAxis
                    yAxisId="right"
                    orientation="right"
                    tick={{ fontSize: 10, fill: '#9ca3af' }}
                    tickLine={false}
                    axisLine={{ stroke: '#d1d5db' }}
                    unit="件"
                  />
                )}
                <Tooltip content={<CustomTooltipMonthly />} />
                <Legend
                  iconType="circle"
                  iconSize={7}
                  formatter={(value: string) => (
                    <span className="text-xs text-gray-600 dark:text-gray-300">
                      {value === 'hours' ? '学習時間' : '投稿数'}
                    </span>
                  )}
                />
                <Area
                  yAxisId="left"
                  type="monotone"
                  dataKey="hours"
                  stroke="#0d9488"
                  strokeWidth={2.5}
                  fill="url(#monthlyHoursGrad)"
                  dot={{ r: isMobile ? 2 : 4, fill: '#0d9488', strokeWidth: 2, stroke: '#fff' }}
                  activeDot={{ r: isMobile ? 4 : 6 }}
                />
                <Area
                  yAxisId={isMobile ? 'left' : 'right'}
                  type="monotone"
                  dataKey="posts"
                  stroke="#f59e0b"
                  strokeWidth={2}
                  fill="url(#monthlyPostsGrad)"
                  dot={{ r: isMobile ? 2 : 3, fill: '#f59e0b', strokeWidth: 2, stroke: '#fff' }}
                  activeDot={{ r: isMobile ? 3 : 5 }}
                />
              </AreaChart>
            </ResponsiveContainer>
          </div>
        </div>
      )}

      {/* Row 4: Subject Monthly Stacked Bar */}
      {subjectMonthly && subjectMonthly.length > 0 && subjectKeys && subjectKeys.length > 0 && (
        <div className="bg-gray-100 dark:bg-gray-800 rounded-xl border border-gray-300 dark:border-gray-700 p-4 sm:p-5">
          <h3 className="text-sm font-semibold text-gray-700 dark:text-gray-200 mb-0.5">
            科目別の月次推移
          </h3>
          <p className="text-xs text-gray-400 dark:text-gray-500 mb-3">
            直近12ヶ月の科目ごとの学習時間（積み上げ）
          </p>
          <div style={{ height: chartHeightLg }}>
            <ResponsiveContainer width="100%" height="100%">
              <BarChart
                data={subjectMonthly}
                margin={{ top: 5, right: 4, left: isMobile ? -20 : -15, bottom: 5 }}
              >
                <CartesianGrid strokeDasharray="3 3" stroke="#e5e7eb" />
                <XAxis
                  dataKey="month"
                  tick={{ fontSize: isMobile ? 9 : 10, fill: '#9ca3af' }}
                  tickLine={false}
                  axisLine={{ stroke: '#d1d5db' }}
                  interval={monthlyInterval}
                />
                <YAxis
                  tick={{ fontSize: isMobile ? 9 : 10, fill: '#9ca3af' }}
                  tickLine={false}
                  axisLine={{ stroke: '#d1d5db' }}
                  unit="h"
                  width={isMobile ? 28 : 35}
                />
                <Tooltip content={<CustomTooltipStacked />} />
                <Legend
                  iconType="circle"
                  iconSize={7}
                  wrapperStyle={isMobile ? { fontSize: '10px' } : {}}
                  formatter={(value: string) => (
                    <span className="text-xs text-gray-600 dark:text-gray-300">{value}</span>
                  )}
                />
                {subjectKeys.map((key, index) => (
                  <Bar
                    key={key}
                    dataKey={key}
                    stackId="subjects"
                    fill={STACKED_COLORS[index % STACKED_COLORS.length]}
                    radius={index === subjectKeys.length - 1 ? [3, 3, 0, 0] : [0, 0, 0, 0]}
                    maxBarSize={isMobile ? 24 : 40}
                  />
                ))}
              </BarChart>
            </ResponsiveContainer>
          </div>
        </div>
      )}

      {/* Row 5: Weekly Trend */}
      {weeklyTrend && weeklyTrend.length > 0 && (
        <div className="bg-gray-100 dark:bg-gray-800 rounded-xl border border-gray-300 dark:border-gray-700 p-4 sm:p-5">
          <h3 className="text-sm font-semibold text-gray-700 dark:text-gray-200 mb-0.5">
            週別学習トレンド
          </h3>
          <p className="text-xs text-gray-400 dark:text-gray-500 mb-3">
            直近12週間の学習時間と投稿数の推移
          </p>
          <div style={{ height: chartHeightSm }}>
            <ResponsiveContainer width="100%" height="100%">
              <LineChart
                data={weeklyTrend}
                margin={{ top: 5, right: 4, left: isMobile ? -20 : -15, bottom: 5 }}
              >
                <CartesianGrid strokeDasharray="3 3" stroke="#e5e7eb" />
                <XAxis
                  dataKey="week"
                  tick={{ fontSize: isMobile ? 9 : 10, fill: '#9ca3af' }}
                  tickLine={false}
                  axisLine={{ stroke: '#d1d5db' }}
                  interval={weeklyInterval}
                />
                <YAxis
                  yAxisId="left"
                  tick={{ fontSize: isMobile ? 9 : 10, fill: '#9ca3af' }}
                  tickLine={false}
                  axisLine={{ stroke: '#d1d5db' }}
                  unit="h"
                  width={isMobile ? 28 : 35}
                />
                {!isMobile && (
                  <YAxis
                    yAxisId="right"
                    orientation="right"
                    tick={{ fontSize: 10, fill: '#9ca3af' }}
                    tickLine={false}
                    axisLine={{ stroke: '#d1d5db' }}
                    unit="件"
                  />
                )}
                <Tooltip content={<CustomTooltipWeekly />} />
                <Legend
                  iconType="circle"
                  iconSize={7}
                  formatter={(value: string) => (
                    <span className="text-xs text-gray-600 dark:text-gray-300">
                      {value === 'hours' ? '学習時間' : '投稿数'}
                    </span>
                  )}
                />
                <Line
                  yAxisId="left"
                  type="monotone"
                  dataKey="hours"
                  stroke="#0d9488"
                  strokeWidth={2}
                  dot={{ r: isMobile ? 2 : 3, fill: '#0d9488' }}
                />
                <Line
                  yAxisId={isMobile ? 'left' : 'right'}
                  type="monotone"
                  dataKey="posts"
                  stroke="#f59e0b"
                  strokeWidth={2}
                  dot={{ r: isMobile ? 2 : 3, fill: '#f59e0b' }}
                />
              </LineChart>
            </ResponsiveContainer>
          </div>
        </div>
      )}

      {/* Subject breakdown table */}
      {subjectData.length > 0 && (
        <div className="bg-gray-100 dark:bg-gray-800 rounded-xl border border-gray-300 dark:border-gray-700 p-4 sm:p-5">
          <h3 className="text-sm font-semibold text-gray-700 dark:text-gray-200 mb-3">
            科目別学習時間の詳細
          </h3>
          <div className="overflow-x-auto -mx-1">
            <table className="w-full text-sm min-w-[280px]">
              <thead>
                <tr className="border-b border-gray-300 dark:border-gray-600">
                  <th className="text-left py-2 px-2 sm:px-3 text-xs font-medium text-gray-500 dark:text-gray-400">科目</th>
                  <th className="text-right py-2 px-2 sm:px-3 text-xs font-medium text-gray-500 dark:text-gray-400">時間</th>
                  <th className="text-right py-2 px-2 sm:px-3 text-xs font-medium text-gray-500 dark:text-gray-400">割合</th>
                  <th className="text-left py-2 px-2 sm:px-3 text-xs font-medium text-gray-500 dark:text-gray-400 hidden sm:table-cell">グラフ</th>
                </tr>
              </thead>
              <tbody>
                {sortedSubjectData.map((item, index) => {
                  const total = subjectData.reduce((s, d) => s + d.value, 0);
                  const pct = total > 0 ? Math.round((item.value / total) * 100) : 0;
                  return (
                    <tr key={item.name} className="border-b border-gray-200 dark:border-gray-700 last:border-0">
                      <td className="py-2 px-2 sm:px-3">
                        <div className="flex items-center gap-1.5">
                          <div
                            className="w-2 h-2 rounded-full shrink-0"
                            style={{ backgroundColor: COLORS[index % COLORS.length] }}
                          />
                          <span className="text-xs sm:text-sm text-gray-700 dark:text-gray-200 truncate max-w-[80px] sm:max-w-none">
                            {item.name}
                          </span>
                        </div>
                      </td>
                      <td className="py-2 px-2 sm:px-3 text-right text-xs sm:text-sm font-medium text-gray-800 dark:text-gray-100 whitespace-nowrap">
                        {item.value}h
                      </td>
                      <td className="py-2 px-2 sm:px-3 text-right text-xs sm:text-sm text-gray-500 dark:text-gray-400">
                        {pct}%
                      </td>
                      <td className="py-2 px-2 sm:px-3 hidden sm:table-cell">
                        <div className="w-full bg-gray-200 dark:bg-gray-700 rounded-full h-2">
                          <div
                            className="h-2 rounded-full transition-all"
                            style={{ width: `${pct}%`, backgroundColor: COLORS[index % COLORS.length] }}
                          />
                        </div>
                      </td>
                    </tr>
                  );
                })}
              </tbody>
            </table>
          </div>
        </div>
      )}
    </div>
  );
}
